import { Description, InputGroup, Label } from '@heroui/react';
import { condString, type Nullable } from '@silverhand/essentials';
import classNames from 'classnames';
import type { HTMLProps, ReactElement, Ref } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ErrorMessage from '@/shared/components/ErrorMessage';
import useShake from '@/shared/hooks/use-shake';

export type Props = Omit<HTMLProps<HTMLInputElement>, 'prefix' | 'ref' | 'label'> & {
  readonly className?: string;
  readonly inputFieldClassName?: string;
  readonly errorMessage?: string;
  readonly isDanger?: boolean;
  readonly prefix?: ReactElement;
  readonly isPrefixVisible?: boolean;
  readonly suffix?: ReactElement;
  readonly isSuffixFocusVisible?: boolean;
  readonly label?: ReactElement | string;
  readonly description?: Nullable<string>;
  readonly ref?: Ref<Nullable<HTMLInputElement>>;
};

export const getInputLabelId = (inputId: string) => `${inputId}-label`;

/** Grows a small in-field button's touch area to 44px without making the field taller. */
export const fieldButtonHitAreaClass =
  "after:absolute after:-inset-1 after:content-[''] md:after:-inset-1.5";

/**
 * A plain `InputGroup` rather than a HeroUI `TextField`: `TextField` always controls the input's
 * value, which would fight react-hook-form's uncontrolled `register` (it writes `input.value`
 * directly on reset/default values).
 */
const InputField = ({
  className,
  inputFieldClassName,
  errorMessage,
  isDanger,
  prefix,
  suffix,
  isPrefixVisible: _isPrefixVisible,
  isSuffixFocusVisible,
  label,
  description,
  required = true,
  ref,
  ...props
}: Props) => {
  const { t } = useTranslation();

  const errorMessages = errorMessage ? errorMessage.split('\n') : undefined;
  const fieldRef = useRef<HTMLDivElement>(null);
  // InputGroup keeps its own ref on the group element, so the shake target is found from the
  // wrapper instead.
  const groupRef = useMemo(
    () => ({
      get current() {
        return fieldRef.current?.querySelector<HTMLElement>('.t-input') ?? null;
      },
    }),
    []
  );
  // Keyed on the submit count too, so a retry that fails with the same message still shakes.
  const [submitCount, setSubmitCount] = useState(0);
  useEffect(() => {
    const form = fieldRef.current?.closest('form');
    const onSubmit = () => {
      setSubmitCount((count) => count + 1);
    };
    form?.addEventListener('submit', onSubmit);

    return () => {
      form?.removeEventListener('submit', onSubmit);
    };
  }, []);
  useShake(groupRef, errorMessage && `${submitCount}:${errorMessage}`);

  const generatedId = useId();
  const inputId = props.id ?? generatedId;
  const labelId = getInputLabelId(inputId);
  const descriptionId = `${inputId}-description`;
  const errorId = `${inputId}-error`;
  // IOS VoiceOver ignores aria-errormessage, so the error is also part of the description.
  const describedBy =
    [description && descriptionId, errorMessages && errorId].filter(Boolean).join(' ') || undefined;

  const labelWithOptionalSuffix = required
    ? label
    : condString(label && t('input.label_with_optional', { label }));

  return (
    <div
      ref={fieldRef}
      className={classNames('flex w-full flex-col gap-1', className)}
      data-invalid={isDanger ? 'true' : undefined}
    >
      {label && (
        <Label id={labelId} htmlFor={inputId}>
          {labelWithOptionalSuffix}
        </Label>
      )}
      <InputGroup
        fullWidth
        isInvalid={Boolean(isDanger)}
        isDisabled={Boolean(props.disabled)}
        className={classNames(
          't-input',
          isSuffixFocusVisible && 'group/field',
          inputFieldClassName
        )}
      >
        {prefix && (
          // The group focuses its input on any click inside it; a prefix control (the country
          // picker) must not pull the on-screen keyboard up while it opens its own overlay.
          <InputGroup.Prefix
            className="ps-2 pe-0 animate-in fade-in slide-in-from-start-2 duration-(--duration-fast) ease-(--ease-smooth-out) motion-reduce:slide-in-from-start-0"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {prefix}
          </InputGroup.Prefix>
        )}
        <InputGroup.Input
          {...props}
          ref={ref}
          id={inputId}
          aria-invalid={isDanger ? 'true' : 'false'}
          aria-describedby={describedBy}
          aria-errormessage={errorMessages ? errorId : undefined}
          className={classNames('min-w-0', prefix && 'ps-3')}
        />
        {suffix && (
          <InputGroup.Suffix
            className={classNames(
              'pe-1',
              isSuffixFocusVisible &&
                'pointer-events-none scale-90 opacity-0 transition-[opacity,scale] duration-(--duration-quick) ease-(--ease-out-strong) group-focus-within/field:pointer-events-auto group-focus-within/field:scale-100 group-focus-within/field:opacity-100 motion-reduce:transition-opacity'
            )}
          >
            {suffix}
          </InputGroup.Suffix>
        )}
      </InputGroup>
      {description && <Description id={descriptionId}>{description}</Description>}
      {errorMessages && (
        <ErrorMessage id={errorId}>
          {errorMessages.length > 1 ? (
            <ul>
              {errorMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          ) : (
            errorMessages[0]
          )}
        </ErrorMessage>
      )}
    </div>
  );
};

export default InputField;
